/**
 * Keeps refill jars in the cart on the right Progus plan: the cheaper plan from N refill jars, the regular one below.
 *
 * Progus can not price a subscription by quantity, so the lower price from 2 jars is a second plan. The product page
 * adds a tier on the right plan; this script holds the cart to the same rule when the customer changes quantities in
 * the cart (one jar would otherwise keep the lower price, two jars added one by one would miss it). The lines and the
 * plans come from sections/cart-refills-state over the Section Rendering API, the same way assets/cart-gift.js works.
 */

const ROOT = window.Shopify?.routes?.root ?? '/';
const SECTION = 'cart-refills-state';
const MAX_ROUNDS = 6;

let syncing = false;
let syncAgain = false;

async function readState() {
  const response = await fetch(`${ROOT}?sections=${SECTION}`);

  if (!response.ok) return null;

  const markup = (await response.json())[SECTION];

  if (!markup) return null;

  const element = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-cart-refills-state]');

  if (!element) return null;

  return {
    multiFrom: Number(element.dataset.multiFrom) || 0,
    lines: Array.from(element.querySelectorAll('[data-refill-line]'), (line) => ({
      key: line.dataset.key,
      quantity: Number(line.dataset.quantity) || 0,
      plan: line.dataset.plan,
      single: line.dataset.single,
      multi: line.dataset.multi,
    })),
  };
}

/** The first line that is on the wrong plan for the cart's number of refill jars, with the plan it should be on */
function nextChange(state) {
  if (!state || state.multiFrom <= 0 || state.lines.length === 0) return null;

  // Jars are counted per pair of plans: the day creams share one pair, so their scents add up, while Sensitive Skin
  // and Anti-Aging have their own (one Sensitive Skin jar next to a day cream stays on its single jar plan)
  const jarsByPlans = new Map();

  for (const line of state.lines) {
    jarsByPlans.set(line.single, (jarsByPlans.get(line.single) || 0) + line.quantity);
  }

  for (const line of state.lines) {
    const target = jarsByPlans.get(line.single) >= state.multiFrom ? line.multi : line.single;
    if (line.plan !== target) return { line, target };
  }

  return null;
}

/** As the theme mutates the cart: re-render the bundled sections and announce the new cart */
async function changeLine(key, quantity, sellingPlan) {
  const sections = [];

  document.documentElement.dispatchEvent(
    new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
  );

  const response = await fetch(`${ROOT}cart/change.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity, selling_plan: Number(sellingPlan), sections: sections.join(',') }),
  });

  if (!response.ok) return null;

  const cart = await response.json();

  document.documentElement.dispatchEvent(
    new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'cart-refills', cart } })
  );

  return cart;
}

async function sync() {
  if (syncing) {
    syncAgain = true;
    return;
  }

  syncing = true;
  let mutated = false;

  try {
    // One line per round: a plan change gives the line a new key, so the state is read again before the next one
    for (let round = 0; round < MAX_ROUNDS; round++) {
      const change = nextChange(await readState());

      if (!change) break;

      if (!(await changeLine(change.line.key, change.line.quantity, change.target))) break;

      mutated = true;
    }

    // The cart page reloads on line changes instead of re-rendering sections; one reload shows the new prices
    if (mutated && window.themeVariables?.settings?.pageType === 'cart') window.location.reload();
  } catch (error) {
    console.warn('[cart-refills] Could not update the refill plans.', error);
  } finally {
    syncing = false;

    if (syncAgain) {
      syncAgain = false;
      sync();
    }
  }
}

document.addEventListener('cart:change', (event) => {
  // Its own changes come back as cart:change too; the state is settled by then, the next read just confirms it
  if (event.detail?.baseEvent === 'cart-refills') return;
  sync();
});
document.addEventListener('cart:refresh', sync);
sync();
